<script setup lang="ts">
/**
 * A decision's summary under its own headings ("What changed", "Watch out for"): each heading a system
 * label on a hairline, its lines under it. Drawn inside the decision card, and wherever the portal shows a
 * gate's summary beside its controls (the chat, the run page), so a summary reads the same everywhere.
 */
import type { DecisionSection } from '../lib/surfaces'

defineProps<{ sections: readonly DecisionSection[] }>()
</script>

<template>
    <div class="zh-summary">
        <section v-for="(section, i) in sections" :key="section.heading ?? i" class="zh-summary__section">
            <h3 v-if="section.heading" class="zh-summary__heading">{{ section.heading }}</h3>
            <ul class="zh-summary__lines">
                <li v-for="(line, j) in section.lines" :key="j">{{ line }}</li>
            </ul>
        </section>
    </div>
</template>

<style scoped>
.zh-summary {
    display: grid;
    gap: 0.5rem;
}

.zh-summary__section {
    display: grid;
    gap: 0.25rem;
    padding-top: 0.625rem;
    border-top: 1px solid var(--hair);
}

/* The system label (BG §19.3). */
.zh-summary__heading {
    margin: 0;
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-summary__lines {
    display: grid;
    gap: 0.25rem;
    margin: 0;
    padding: 0;
    color: var(--ink-2);
    font-size: 0.9375rem;
    list-style: none;
}

.zh-summary__lines li::before {
    content: "–\00a0" / "";
    color: var(--ink-3);
}
</style>
